
import React from 'react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { BookOpen, AlertTriangle, FileText, HelpCircle, FileWarning, Download, Clock, FileType, RefreshCcw, Database } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { KnowledgeEntry } from './types';
import { ScrollArea } from "@/components/ui/scroll-area";

interface KnowledgeEntryErrorHelpProps {
  errorMessage: string | null;
  originalFilename?: string | null;
  metadata?: KnowledgeEntry['metadata'];
}

export function KnowledgeEntryErrorHelp({ errorMessage, originalFilename, metadata }: KnowledgeEntryErrorHelpProps) {
  // Return nothing if there's no error message
  if (!errorMessage) return null;

  // Extract specific API errors from the metadata if available
  const apiErrorDetails = metadata?.processing_error ? 
    (typeof metadata.processing_error === 'string' ? 
      metadata.processing_error : 
      JSON.stringify(metadata.processing_error, null, 2)) : 
    null;

  // Detect error types based on the error message
  const isUnsupportedFormat = errorMessage.includes('unsupported') || errorMessage.includes('format');
  const isOpenAIError = errorMessage.includes('OpenAI') || errorMessage.includes('embedding');
  const isPDFError = originalFilename?.toLowerCase().endsWith('.pdf') || errorMessage.toLowerCase().includes('pdf');
  const isParsingError = errorMessage.includes('parsing') || errorMessage.includes('is not a function');
  const isFileSizeError = errorMessage.includes('too large') || errorMessage.includes('file size') || errorMessage.includes('size limit');
  const isJobTimeoutError = errorMessage.includes('timed out');
  const isAsyncError = errorMessage.includes('asynchronous') || errorMessage.includes('async');
  const isDocStructureError = errorMessage.includes('structure') || errorMessage.includes('layout') || 
                              (isPDFError && (errorMessage.includes('text extraction') || errorMessage.includes('content')));
  const isApiParamError = errorMessage.includes('parameter') || errorMessage.includes('invalid_request_error') || 
                          (apiErrorDetails && (apiErrorDetails.includes('parameter') || apiErrorDetails.includes('invalid_value')));
  
  // Try to extract and format the OpenAI API error JSON if it exists
  const extractOpenAIError = () => {
    if (!errorMessage) return null;
    
    const jsonMatch = errorMessage.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        const errorJson = JSON.parse(jsonMatch[0]);
        return errorJson;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  
  const openAIErrorJson = extractOpenAIError();
  
  const getErrorIcon = () => {
    if (isOpenAIError) return <AlertTriangle className="h-5 w-5 text-amber-500" />;
    if (isParsingError) return <FileText className="h-5 w-5 text-blue-500" />;
    if (isFileSizeError) return <FileWarning className="h-5 w-5 text-orange-500" />;
    if (isJobTimeoutError) return <Clock className="h-5 w-5 text-purple-500" />;
    if (isDocStructureError) return <FileType className="h-5 w-5 text-indigo-500" />;
    if (isApiParamError) return <Database className="h-5 w-5 text-red-500" />;
    return <AlertTriangle className="h-5 w-5 text-red-500" />;
  };

  const getErrorTitle = () => {
    if (isParsingError) return "Document Parsing Issue";
    if (isUnsupportedFormat) return "Document Format Issue";
    if (isOpenAIError) return "OpenAI Processing Error";
    if (isFileSizeError) return "File Size Issue";
    if (isJobTimeoutError) return "Processing Timeout";
    if (isAsyncError) return "Asynchronous Processing Error";
    if (isDocStructureError) return "Document Structure Issue";
    if (isApiParamError) return "API Parameter Error";
    return "Processing Error";
  };

  const getTroubleshootingSteps = () => {
    if (isApiParamError) {
      return (
        <>
          <p className="font-medium mt-3">There was an error with the API parameters when processing this document.</p>
          {openAIErrorJson && (
            <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-md text-sm font-mono overflow-x-auto">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">OpenAI API Error Details:</p>
              <ScrollArea className="max-h-48">
                <pre className="whitespace-pre-wrap text-xs">
                  {JSON.stringify(openAIErrorJson, null, 2)}
                </pre>
              </ScrollArea>
            </div>
          )}
          <p className="mt-2">This error indicates an issue with how the API is being called. The specific details above should help with debugging.</p>
        </>
      );
    } else if (isJobTimeoutError) {
      return (
        <>
          <p className="font-medium mt-3">The document processing took too long and timed out.</p>
          <p className="mt-2">Try these solutions:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Split the document into smaller files (under 100MB per file)</li>
            <li>Remove unnecessary images or complex formatting</li>
            <li>Convert the document to a simpler format</li>
            <li>Try uploading during less busy times</li>
          </ul>
        </>
      );
    } else if (isUnsupportedFormat) {
      return (
        <>
          <p className="font-medium mt-3">Your document format isn't compatible with OpenAI's processing system.</p>
          <p className="mt-2">Try these solutions:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Open and re-save the PDF using Adobe Acrobat or another PDF editor</li>
            <li>Ensure the file has the correct extension (e.g., .pdf, .docx, .txt)</li>
            <li>Check if the PDF is password-protected or has security restrictions</li>
            <li>Try flattening the PDF (removing layers and interactive elements)</li>
            <li>If it's a scanned document, ensure it has sufficient resolution</li>
          </ul>
        </>
      );
    } else if (isParsingError || isDocStructureError) {
      return (
        <>
          <p className="font-medium mt-3">The document was processed but we encountered issues with its structure or layout.</p>
          <p className="mt-2">While the document has been saved, the parsed content may appear fragmented or disorganized. You can try:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Converting the PDF to a simpler format with cleaner text</li>
            <li>If the document has tables or columns, try a version with simpler layout</li>
            <li>For technical service manuals, try individual sections rather than complete manuals</li>
            <li>Use the "View Original Document" option to see the content directly</li>
            <li>Try different document types (PDF vs. Word) if available</li>
          </ul>
        </>
      );
    } else if (isAsyncError) {
      return (
        <>
          <p className="font-medium mt-3">There was an error during the asynchronous processing of your document.</p>
          <p className="mt-2">Try these solutions:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Ensure your document doesn't exceed 512MB in size</li>
            <li>Check if your PDF has unusual security settings or encryption</li>
            <li>Try converting to a different format (PNG, JPEG) if it's a simple document</li>
            <li>Reduce the complexity of the document (fewer pages or elements)</li>
            <li>Wait a few minutes and try the upload again</li>
          </ul>
        </>
      );
    } else if (isOpenAIError) {
      return (
        <>
          <p className="font-medium mt-3">There was an error when calling the OpenAI API to process this document.</p>
          <p className="mt-2">This is typically caused by:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Incorrect API parameters in the request</li>
            <li>Invalid format for one of the request parameters</li>
            <li>Temporary issues with the OpenAI service</li>
            <li>API key limitations or quotas</li>
          </ul>
          {openAIErrorJson && (
            <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
              <p className="text-xs font-medium mb-1">Error Details:</p>
              <pre className="text-xs font-mono whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(openAIErrorJson, null, 2)}
              </pre>
            </div>
          )}
        </>
      );
    }
    
    return null;
  };

  const getDocsLink = () => {
    if (isOpenAIError || isUnsupportedFormat || isFileSizeError || isAsyncError || isApiParamError) {
      return (
        <Button variant="outline" size="sm" className="gap-1" asChild>
          <a href="https://platform.openai.com/docs/assistants/tools/file-search/supported-files" target="_blank" rel="noopener noreferrer">
            <BookOpen className="h-4 w-4" />
            OpenAI Supported Files
          </a>
        </Button>
      );
    }
    
    return null;
  };

  return (
    <Alert variant="destructive" className="mt-4">
      <div className="flex gap-3">
        {getErrorIcon()}
        <AlertTitle>{getErrorTitle()}</AlertTitle>
      </div>
      <AlertDescription className="mt-2">
        {!openAIErrorJson && errorMessage}
        {getTroubleshootingSteps()}
        
        <div className="flex flex-wrap gap-2 mt-4">
          {getDocsLink()}
          
          <Button variant="outline" size="sm" className="gap-1" asChild>
            <a href="https://platform.openai.com/docs/guides/retrieval" target="_blank" rel="noopener noreferrer">
              <HelpCircle className="h-4 w-4" />
              Troubleshooting
            </a>
          </Button>
          
          {(isUnsupportedFormat) && (
            <Button variant="outline" size="sm" className="gap-1" asChild>
              <a href="https://www.adobe.com/acrobat/online/compress-pdf.html" target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" />
                Compress PDF
              </a>
            </Button>
          )}

          {(isParsingError || isDocStructureError) && (
            <Button variant="outline" size="sm" className="gap-1" asChild>
              <a href="https://www.adobe.com/acrobat/online/pdf-to-word.html" target="_blank" rel="noopener noreferrer">
                <RefreshCcw className="h-4 w-4" />
                Convert PDF Format
              </a>
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
}
